import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useTRPC, useTRPCClient } from '#src/trpc.js';
import { useLatestRevisionId } from '#utils/use-latest-revision-id.js';

const SEED_POLL_INTERVAL_MS = 1500;

export interface LazyDemoSeed {
	/**
	 * - `idle`: nothing started; the sample door should offer to start a seed.
	 * - `seeding`: a clone is being provisioned (or its revision is still resolving);
	 *   show the anticipation state.
	 * - `ready`: the clone and its latest revision are known; navigate to its results.
	 * - `unavailable`: no demo is published, or the seed failed; degrade gracefully.
	 */
	status: 'idle' | 'seeding' | 'ready' | 'unavailable';
	/** The cloned demo project's id once known, else null. */
	clonedProjectId: string | null;
	/** The cloned demo's latest revision id once `status` is `ready`, else null. */
	clonedRevisionId: string | null;
	/** Starts (or retries) seeding the default demo into this org. */
	startSeed: () => void;
}

/**
 * Drives on-demand ("lazy") seeding of the default demo project into an org for
 * the sample onboarding door: start a clone, poll its progress, and surface a
 * terminal state. Query-driven so a mid-seed reload resumes into the running seed.
 *
 * When `observeExistingRun` is set, a seed run started elsewhere (e.g. when the
 * org was created) is watched without being reported: the state stays `idle`, and
 * the projects list is refreshed when that run completes so the clone shows up.
 *
 * @param orgName - The organization to seed the demo into.
 * @param observeExistingRun - Whether to watch for a seed run this hook did not
 *   start. Requires 'manage' on the org.
 * @returns The current seed state and a `startSeed` trigger. See {@link LazyDemoSeed}.
 */
export function useLazyDemoSeed(orgName: string, observeExistingRun: boolean): LazyDemoSeed {
	const trpc = useTRPC();
	const trpcClient = useTRPCClient();
	const queryClient = useQueryClient();
	const [hasStarted, setHasStarted] = useState(false);
	const [failed, setFailed] = useState(false);
	// Each accepted seed run reads its status under its own cache entry, so a read issued
	// before a retry was accepted can never be taken for the retried run, however late it lands.
	const [seedRun, setSeedRun] = useState(0);
	const [awaitingRun, setAwaitingRun] = useState(false);

	const seed = useMutation(trpc.organization.seedDefaultDemo.mutationOptions());

	const { data: seedStatus } = useQuery({
		queryKey: [...trpc.organization.getDemoSeedStatusForOrg.queryKey({ organizationName: orgName }), seedRun],
		queryFn: () => trpcClient.organization.getDemoSeedStatusForOrg.query({ organizationName: orgName }),
		enabled: hasStarted || observeExistingRun,
		// Keep polling on every non-terminal state. `notFound` is a common transient before the
		// workflow becomes visible; stopping on it would strand the UI on the seeding state. An
		// org that was never seeded also reads `notFound`, so only a started seed polls on it.
		refetchInterval: (query) => {
			const current = query.state.data?.status;
			return current === 'running' || (hasStarted && current === 'notFound') ? SEED_POLL_INTERVAL_MS : false;
		},
	});

	const seedRunStatus = seedStatus?.status;
	useEffect(() => {
		if (seedRunStatus === 'completed') {
			void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
		}
	}, [seedRunStatus, queryClient, trpc]);

	const startSeed = () => {
		// Ignore repeated triggers (e.g. the retry notice and the sample door both firing)
		// while a start is already in flight, so we never launch duplicate seed workflows.
		if (seed.isPending) {
			return;
		}
		setFailed(false);
		setHasStarted(true);
		setAwaitingRun(true);
		seed.mutate(
			{ organizationName: orgName },
			{
				onSuccess: (result) => {
					setAwaitingRun(false);
					if (result.workflowId === null && result.skipped.alreadyAdded.length > 0) {
						// The demo landed after the projects list was fetched; refresh it so the
						// door opens the existing clone instead.
						setHasStarted(false);
						void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
						return;
					}
					if (result.workflowId === null) {
						setFailed(true);
						return;
					}
					setSeedRun((run) => run + 1);
				},
				onError: () => {
					setAwaitingRun(false);
					setFailed(true);
				},
			},
		);
	};

	const startedSeedStatus = hasStarted && !awaitingRun ? seedStatus : undefined;

	const clonedProjectId =
		startedSeedStatus?.status === 'completed' ? (startedSeedStatus.perProject[0]?.clonedProjectId ?? null) : null;

	// Resolve the clone's latest revision so the sample door can land straight on its results view.
	const clonedRevisionId = useLatestRevisionId(clonedProjectId);

	const status = ((): LazyDemoSeed['status'] => {
		if (failed) {
			return 'unavailable';
		}
		if (!hasStarted) {
			return 'idle';
		}
		if (startedSeedStatus?.status === 'failed') {
			return 'unavailable';
		}
		if (startedSeedStatus?.status === 'completed') {
			if (clonedProjectId === null) {
				return 'unavailable';
			}
			// Stay in the anticipation state until the revision id resolves, so navigation
			// always has both ids and never lands on a half-loaded route.
			return clonedRevisionId !== null ? 'ready' : 'seeding';
		}
		return 'seeding';
	})();

	return { status, clonedProjectId, clonedRevisionId, startSeed };
}
